import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getMovementDetail, cancelMovement } from "@/lib/api";
import { getCurrentShop } from "@/lib/auth";
import { Loader2, FileText } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { exportToExcel } from "@/lib/exportToExcel";

const TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  entry: { label: "Entrada", icon: "📥", color: "bg-green-100 text-green-800" },
  exit: { label: "Salida", icon: "📤", color: "bg-red-100 text-red-800" },
  waste: { label: "Merma", icon: "🗑️", color: "bg-orange-100 text-orange-800" },
  adjustment: { label: "Ajuste", icon: "🟡", color: "bg-yellow-100 text-yellow-800" },
};

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  confirmed: { label: "Confirmada", variant: "default" },
  draft: { label: "Borrador", variant: "secondary" },
  cancelled: { label: "Cancelada", variant: "destructive" },
};

export function DetailModal({
  movementId,
  show,
  onClose,
  onUpdate,
}: {
  movementId: string | null;
  show: boolean;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const [movement, setMovement] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const currentShop = getCurrentShop()!;

  useEffect(() => {
    if (show && movementId) {
      loadMovement();
    }
  }, [show, movementId]);

  const loadMovement = async () => {
    if (!movementId) return;
    setLoading(true);
    try {
      const data = await getMovementDetail(currentShop.id, movementId);
      setMovement(data);
    } catch {
      toast({ title: "Error al cargar detalle", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!movement?.lines?.length) return;
    const formatted = movement.lines.map((line: any, i: number) => ({
      "#": i + 1,
      "Producto": line.product?.name || "—",
      "Cantidad": line.quantity,
      "Costo Und": `$${parseFloat(line.unitCost || "0").toFixed(2)}`,
      "Total": `$${parseFloat(line.totalCost || "0").toFixed(2)}`,
      "Stock Ant.": line.previousStock ?? "—",
      "Stock Desp.": line.newStock ?? "—",
      "Lote": line.batchNumber || "—",
    }));
    await exportToExcel(
      formatted,
      `movimiento_${movement.sequential}_${new Date().toISOString().split("T")[0]}`,
      "Movimiento",
    );
  };

  const handleCancel = async () => {
    if (!movementId) return;
    setCancelling(true);
    try {
      await cancelMovement(currentShop.id, movementId);
      toast({ title: "Movimiento cancelado" });
      onUpdate();
      loadMovement();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  const typeInfo = TYPE_LABELS[movement?.type] || { label: movement?.type, icon: "📦", color: "" };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {typeInfo.icon} Detalle: {movement?.sequential || "Cargando..."}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && movement && (
          <div className="space-y-4 py-4 min-w-0">
            {/* Header info */}
            <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
              <div className="flex items-center gap-2 mb-2">
                <span className={typeInfo.color + " px-2 py-0.5 rounded text-xs font-medium"}>
                  {typeInfo.icon} {typeInfo.label}
                </span>
                <Badge variant={STATUS_BADGE[movement.status]?.variant || "outline"}>
                  {STATUS_BADGE[movement.status]?.label || movement.status}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Fecha:</span> {movement.createdAt ? new Date(movement.createdAt).toLocaleDateString("es-CU") : "—"}</div>
                <div><span className="text-muted-foreground">Total:</span> ${parseFloat(movement.totalCost || "0").toFixed(2)}</div>
                {movement.referenceType && <div><span className="text-muted-foreground">Referencia:</span> {movement.referenceType} {movement.referenceNumber || ""}</div>}
                {movement.supplier?.name && <div><span className="text-muted-foreground">Proveedor:</span> {movement.supplier.name}</div>}
              </div>
              {movement.notes && (
                <div className="text-sm mt-2">
                  <span className="text-muted-foreground">Notas:</span> {movement.notes}
                </div>
              )}
            </div>

            {/* Lines table */}
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-[650px] text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-1 sm:p-2 text-left">Producto</th>
                    <th className="p-1 sm:p-2 text-right">Cant</th>
                    <th className="p-1 sm:p-2 text-right">Costo</th>
                    <th className="p-1 sm:p-2 text-right">Total</th>
                    <th className="p-1 sm:p-2 text-right hidden sm:table-cell">Stock ant.</th>
                    <th className="p-1 sm:p-2 text-right hidden sm:table-cell">Stock desp.</th>
                    <th className="p-1 sm:p-2 text-center hidden sm:table-cell">Lote</th>
                  </tr>
                </thead>
                <tbody>
                  {movement.lines?.map((line: any) => (
                    <tr key={line.id} className="border-b">
                      <td className="p-1 sm:p-2 font-medium break-words max-w-[120px] sm:max-w-none">{line.product?.name || line.productId?.slice(0, 8) || "—"}</td>
                      <td className="p-1 sm:p-2 text-right whitespace-nowrap">{line.quantity > 0 ? `+${line.quantity}` : line.quantity}</td>
                      <td className="p-1 sm:p-2 text-right whitespace-nowrap">${parseFloat(line.unitCost || "0").toFixed(2)}</td>
                      <td className="p-1 sm:p-2 text-right whitespace-nowrap">${parseFloat(line.totalCost || "0").toFixed(2)}</td>
                      <td className="p-1 sm:p-2 text-right hidden sm:table-cell">{line.previousStock ?? "—"}</td>
                      <td className="p-1 sm:p-2 text-right hidden sm:table-cell">{line.newStock ?? "—"}</td>
                      <td className="p-1 sm:p-2 text-center text-xs text-muted-foreground hidden sm:table-cell">{line.batchNumber || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/30 font-medium">
                    <td className="p-1 sm:p-2">{movement.lines?.length || 0} producto(s)</td>
                    <td className="p-1 sm:p-2 text-right">{movement.totalQuantity}</td>
                    <td className="hidden sm:table-cell"></td>
                    <td className="p-1 sm:p-2 text-right">${parseFloat(movement.totalCost || "0").toFixed(2)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Export + Actions */}
            <div className="flex justify-between items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={!movement?.lines?.length}
              >
                <FileText className="mr-2 h-4 w-4 text-green-600" />
                Exportar Excel
              </Button>
              {movement.status === "confirmed" && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelando..." : "❌ Cancelar entrada"}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
